import { Box, MenuItem, MenuList, Modal, Typography } from "@mui/material";
import { useAppSelector } from "app/hooks";
import { OLClearIcon } from "components/CustomIcons/OLClearIcon";
import { Search } from "components/FormControls/Select/Search";
import { OLTabs } from "components/Tabs/OLTabs";
import { useMessage } from "hooks/intl";
import { useDebounce } from "hooks/useDebounce";
import { kebabCase, map, uniq, uniqWith } from "lodash";
import { useEffect, useState } from "react";
import { selectCurrentStoreData } from "reduxState/currentStoreData/currentStoreDataSlice";
import { selectDefaultWalletData } from "reduxState/defaultWalletData/defaultWalletDataSlice";
import { CampaignCondition, SelectedSchemaCondition } from "types/campaigns";
import { useStyles } from "./CommandBar.styles";

interface Props {
  conditions: any[];
  defaultPopularConditions?: any[];
  isOpen: boolean;
  onClose: () => void;
  onItemClick: (item: ConditionWithCounter) => void;
  storagedConditionsName?: string;
  placeholder?: string;
  hasCategories?: boolean;
  "data-test-id"?: string;
}

const popularConditionsTriggerValue = 3;

export type ConditionWithCounter =
  | (CampaignCondition & { counter: number })
  | (SelectedSchemaCondition & { counter: number });

export const CommandBar = ({
  conditions,
  defaultPopularConditions,
  isOpen,
  onClose,
  onItemClick,
  storagedConditionsName,
  placeholder,
  hasCategories = true,
  "data-test-id": dataTestId,
}: Props) => {
  const listOfUsedCategories = uniq([
    "popularConditions",
    ...map(conditions, "category"),
    // get rid of undefined
  ]).filter((item) => item);

  type TabLabel = (typeof listOfUsedCategories)[number];

  const { message } = useMessage();
  const classes = useStyles();
  const { currency } = useAppSelector(selectCurrentStoreData);
  const defaultWallet = useAppSelector(selectDefaultWalletData);

  const storedPopularConditions: ConditionWithCounter[] =
    (storagedConditionsName &&
      JSON.parse(localStorage.getItem(storagedConditionsName) as string)?.map(
        (storedCondition: { counter: number }) => ({
          ...storedCondition,
          counter: storedCondition.counter || popularConditionsTriggerValue,
        }),
      )) ||
    defaultPopularConditions?.map((storedCondition) => ({
      ...storedCondition,
      counter: popularConditionsTriggerValue,
    }));

  const getSearchedConditions = (conditionsArray: ConditionWithCounter[]) => {
    return uniqWith(
      conditionsArray,
      (arrVal, othVal) => arrVal.label === othVal.label,
    ).filter((condition) => {
      const label = message(
        { id: condition.label },
        {
          walletName: defaultWallet?.name,
          units: defaultWallet?.unitPluralName,
          currency,
        },
      );

      return label?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    });
  };

  const [activeTab, setActiveTab] = useState<TabLabel | false>(
    listOfUsedCategories[0],
  );
  const [searchedValue, setSearchedValue] = useState("");
  const [popularConditions, setPopularConditions] = useState<
    ConditionWithCounter[]
  >(storedPopularConditions);
  const { debouncedValue: searchQuery, isTyping } = useDebounce(searchedValue);

  useEffect(() => {
    if (hasCategories && storagedConditionsName) {
      localStorage.setItem(
        storagedConditionsName,
        JSON.stringify(popularConditions),
      );
    }
  }, [hasCategories, popularConditions, storagedConditionsName]);

  const handleConditionClick = (item: ConditionWithCounter) => {
    onItemClick(item);

    if (!hasCategories) {
      setSearchedValue("");
      onClose();
      return;
    }

    const existingCondition = popularConditions.find(
      (condition) => condition.label === item.label,
    );

    if (existingCondition) {
      const updatedConditions = popularConditions.map((condition) =>
        condition.label === item.label
          ? { ...condition, counter: condition.counter + 1 }
          : condition,
      );
      const updatedItem = updatedConditions.find(
        (condition) => condition.label === existingCondition.label,
      );

      if (
        updatedItem &&
        updatedItem.counter === popularConditionsTriggerValue
      ) {
        setPopularConditions([
          updatedItem,
          ...popularConditions.filter(
            (condition) => condition.label !== updatedItem.label,
          ),
        ]);
      } else {
        setPopularConditions(updatedConditions);
      }
    } else {
      setPopularConditions([...popularConditions, { ...item, counter: 1 }]);
    }
    setActiveTab(listOfUsedCategories[0]);
    setSearchedValue("");
    onClose();
  };

  const createConditionMenuItem = (
    item: ConditionWithCounter,
    index?: number,
  ) => {
    return (
      <MenuItem
        className={classes.menuItem}
        key={item.attribute}
        onClick={() => handleConditionClick(item)}
        data-test-id={dataTestId && `${dataTestId}-${kebabCase(item.label)}`}
      >
        {message(
          { id: item.label },
          {
            walletName: defaultWallet?.name,
            units: defaultWallet?.unitPluralName,
            currency,
          },
        )}
      </MenuItem>
    );
  };

  return (
    <Modal
      className={classes.modal}
      onClose={() => {
        setActiveTab(listOfUsedCategories[0]);
        setSearchedValue("");
        onClose();
      }}
      open={isOpen}
    >
      <Box className={classes.modalContent}>
        <Box className={classes.searchAndCloseWrapper}>
          <Search
            className={classes.search}
            data-test-id="commandbar"
            progressClassName={classes.loader}
            placeholder={message({ id: placeholder || "searchForConditions" })}
            searchedValue={searchedValue}
            setSearchedValue={setSearchedValue}
            isLoading={isTyping}
            onInputClick={() => setActiveTab(false)}
          />
          <OLClearIcon
            onClick={() => {
              setActiveTab(listOfUsedCategories[0]);
              setSearchedValue("");
              onClose();
            }}
          />
        </Box>
        {hasCategories && (
          <Typography className={classes.title}>
            {message({ id: "categories" })}
          </Typography>
        )}
        {hasCategories ? (
          <>
            <OLTabs
              className={classes.marginLeft}
              items={listOfUsedCategories}
              value={activeTab}
              onChange={(_: React.SyntheticEvent, newValue: TabLabel) => {
                setSearchedValue("");
                setActiveTab(newValue);
              }}
              data-test-id="tabs"
            />

            <MenuList className={classes.list} role="listbox">
              {activeTab === listOfUsedCategories[0]
                ? getSearchedConditions(popularConditions).map(
                    (item: ConditionWithCounter) =>
                      item.counter >= popularConditionsTriggerValue &&
                      createConditionMenuItem(item),
                  )
                : getSearchedConditions(
                    // show conditions from a category only when not searching - search is empty and not focused (searchedValue is empty and activeTab is selected)
                    !searchedValue && activeTab
                      ? conditions.filter(
                          (condition) => condition.category === activeTab,
                        )
                      : conditions,
                  ).map((item: ConditionWithCounter) =>
                    createConditionMenuItem(item),
                  )}
            </MenuList>
          </>
        ) : (
          getSearchedConditions(conditions).map(
            (item: ConditionWithCounter, index) =>
              createConditionMenuItem(item, index),
          )
        )}
      </Box>
    </Modal>
  );
};
